import { env } from "@/app/_utils/env";

export type OrderSuccessItem = {
  show_title?: string;
  title?: string;
  variant_name?: string | null;
  img_url?: string | null;
  show_date?: string;
  doors_open?: string;
  location_name?: string | null;
  quantity: number;
  amount?: number;
};

export type OrderSuccessResponse = {
  status: string;
  order?: {
    id: string;
    email: string;
    amount: number;
    quantity: number;
    first_name?: string;
    last_name?: string;
    items?: OrderSuccessItem[];
    pdf_url?: string;
  };
};

export type PublicShow = {
  id: string;
  slug?: string | null;
  title: string;
  variant_name: string | null;
  description?: string | null;
  img_url: string | null;
  start: string;
  doors_open?: string | null;
  background_color: string;
  ticket_url: string;
  ticket_price: number | null;
  inventory_quantity: number | null;
  held_quantity?: number;
  next_release_at?: string | null;
  max_quantity_per_order: number;
  checkout_hidden?: boolean;
  checkout_message?: string | null;
  // Aware ISO time; checkout opens itself then and the page counts down to it.
  checkout_opens_at?: string | null;
  shows: any;
  stripe_product_id?: string | null;
  status?: string | null;
  published?: boolean | string | number | null;
  location_name?: string | null;
  location?: { id?: string; name?: string | null } | null;
};

class PublicShowService {
  /** @param options.future — when true, requests `?future=true` (shows with start on or after server “today” window). */
  async getShows(options?: {
    future?: boolean;
    signal?: AbortSignal;
  }): Promise<PublicShow[]> {
    const search = new URLSearchParams();
    if (options?.future) {
      search.set("future", "true");
    }
    const query = search.toString();
    const base = `${env.apiBaseUrl}show/public/`;
    const url = query ? `${base}?${query}` : base;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
      signal: options?.signal,
    });

    if (!res.ok) {
      throw new Error("Failed to fetch shows");
    }

    return res.json();
  }

  async getShowById(id: string): Promise<any> {
    const url = `${env.apiBaseUrl}show/public/?id=${encodeURIComponent(
      id
    )}`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error("Failed to fetch show details");
    }

    return res.json();
  }

  async getShowBySlug(slug: string): Promise<any> {
    const url = `${env.apiBaseUrl}show/public/?slug=${encodeURIComponent(
      slug
    )}`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error("Failed to fetch show details");
    }

    return res.json();
  }

  async getShowSuccessData(id: string): Promise<OrderSuccessResponse> {
    // Trailing slash and no Content-Type: one request per poll, not a 301 plus two CORS preflights.
    const url = `${env.apiBaseUrl}orders/verify/${encodeURIComponent(id)}/`;
    const res = await fetch(url, { method: "GET", cache: "no-store" });

    if (!res.ok) {
      throw new Error("Failed to fetch show details");
    }

    return res.json();
  }

  async downloadTicket(id: string): Promise<Blob> {
    const url = `${env.apiBaseUrl}orders/verify/${encodeURIComponent(
      id
    )}/pdf/`;
    const res = await fetch(url, {
      method: "GET",
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error("Failed to download ticket PDF");
    }

    return res.blob();
  }
}

export default new PublicShowService();

